import { Button as AntdButton, ButtonProps, ConfigProvider, theme } from 'antd';
import React, { forwardRef } from 'react';
import styles from './button.module.css';
import {
  getSessionToken,
  getSlugFromUrl,
  queryClient,
  useFanGroupStore,
} from '@seaters/data-access';
import { hexToRgb } from '../theme/hexToRgb';
import { getColorContrast } from '../theme/getColorContrast';
import Color from 'color';
import {
  baseColor400,
  errorColor700,
  secondaryColor,
  secondaryColor50,
} from '../theme/colors';
import { LookEntity, routes } from '@seaters/constants';

interface CustomButtonProps extends Omit<ButtonProps, 'color' | 'children'> {
  color?: string;
  padding?: number;
  children?: React.ReactNode;
}

export const Button = forwardRef<HTMLButtonElement, CustomButtonProps>(
  ({ color, padding, ...rest }, ref) => {
    const { token } = theme.useToken();

    const slug = getSlugFromUrl();

    const fanGroup: LookEntity | undefined = queryClient.getQueryData([
      'users',
      'look',
    ]);

    const auth = getSessionToken();
    const isAdminRoute =
      typeof window !== 'undefined' &&
      window.location.pathname.includes(`/${routes.admin}`);

    const { fanGroup: storedFanGroup } = useFanGroupStore();

    const fgColorToUse = isAdminRoute
      ? undefined
      : slug
      ? fanGroup?.color
      : auth
      ? storedFanGroup?.color1
      : fanGroup?.color;

    const customColor = fgColorToUse
      ? Color(hexToRgb(`${fgColorToUse}`) ?? 'rgb(0, 0, 0)')
      : isAdminRoute
      ? Color(hexToRgb(secondaryColor) ?? 'rgb(0, 0, 0)')
      : Color(hexToRgb(token.colorPrimary) ?? 'rgb(0, 0, 0)');

    const { colorContrast } = getColorContrast(customColor.rgb().array());

    const customColorDarken = customColor.darken(0.45).toString();

    const isLight = colorContrast === 'black';
    // errorColor700 for danger text: the base errorColor is only 3.68:1 on
    // white, under the 4.5:1 WCAG AA minimum for 14px text. Matches the light
    // theme, whose colorError token is already errorColor700.
    const textColor = rest.disabled
      ? baseColor400
      : rest.type === 'primary'
      ? color === colorContrast
        ? customColor.toString()
        : colorContrast
      : rest.danger
      ? errorColor700
      : color ?? (isLight ? customColorDarken : customColor.toString());

    const borderColor = !rest.type
      ? rest.disabled
        ? baseColor400
        : rest.danger
        ? errorColor700
        : color ?? customColor.toString()
      : rest.disabled && rest.type === 'primary'
      ? baseColor400
      : 'none';

    const restProps = { ...rest };
    delete restProps.style;

    return (
      <ConfigProvider
        theme={{
          token: {
            colorLink: color ?? token.colorLink,
            colorLinkHover: color ?? token.colorLink,
            colorLinkActive: color ?? token.colorLinkActive,
            colorTextBase: color ?? token.colorTextBase,
            colorPrimary: color ?? token.colorPrimary,
            colorBgTextHover: isAdminRoute ? secondaryColor50 : baseColor400,
            colorBgTextActive: isAdminRoute ? secondaryColor50 : baseColor400,
          },
        }}
      >
        <AntdButton
          ref={ref}
          className={styles.button}
          style={{
            padding: padding ?? 'auto',
            color: textColor,
            borderColor: borderColor,
            ...rest.style,
          }}
          {...restProps}
        />
      </ConfigProvider>
    );
  }
);

Button.displayName = 'Button';
